/**
 * @file MapOfAgReport.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-09-29
 * @description Map of Ag: the contacts in that group, the milk processor each
 *              supplies, and the report of what they took in a period.
 *
 *              Two tabs, both owned by Dashboard's TABBED_PAGES strip rather
 *              than rolled here — Contacts and Settings (the processor list).
 *
 *              WHY THE MISSING-FORMULATION STEP COMES FIRST
 *
 *              A delivery of an item with no formulation still has a tonnage,
 *              so the report would happily go out with blank soya and protein
 *              columns — and a processor reading it would take the blank to
 *              mean zero. Preview therefore runs before any document is made,
 *              lists every unformulated item, and opens the add-formulation
 *              modal on each in turn. Generating is still allowed with gaps
 *              outstanding, because sometimes the figures genuinely are not
 *              known yet, but it is a decision rather than an accident.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './InvoiceDetailReport.css';
import './MapOfAgReport.css';
import usePersistedState from '../hooks/usePersistedState';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { useEntityModal } from './EntityModalProvider';
import useDragSelect from '../hooks/useDragSelect';
import TickCheckbox from './TickCheckbox';
import DateInput from './DateInput';
import Dropdown from './Dropdown';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import PaginationControls from './PaginationControls';
import FormulationModal from './FormulationModal';
import ProcessorSettings from './ProcessorSettings';
import FormulationMappingSettings from './FormulationMappingSettings';
import MapOfAgHistory from './MapOfAgHistory';
import FormulationSourceModal from './FormulationSourceModal';
import MissingFormulationsModal from './MissingFormulationsModal';
import MapOfAgExportModal from './MapOfAgExportModal';
import DocumentPreviewModal from './DocumentPreviewModal';
import { useSlideOutButton } from './SlideOutButton';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { formatDateShort } from '../utils/dateFormatters';

const DEFAULT_PAGE_SIZE = 25;

/**
 * Right-click filters, on the two columns where a value repeats. The tick
 * column has nothing to filter and "Last reported" is a date — a list of
 * distinct timestamps is not a filter anyone wants.
 */
const COLUMN_FILTERS = {
  customer: { label: 'Customer', value: (r) => r.company_name || '' },
  processor: { label: 'Processor', value: (r) => r.processor_name || 'Not assigned' }
};

const SORTERS = {
  customer: (r) => String(r.company_name || '').toLowerCase(),
  processor: (r) => String(r.processor_name || '').toLowerCase(),
  reported: (r) => (r.last_reported_at ? new Date(r.last_reported_at).getTime() : -1)
};

/** A quantity of tonnes, to the 4dp the processors are given. */
const t4 = (n) => (n === null || n === undefined || n === ''
  ? '—'
  : Number(n).toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 }));

/**
 * A header that sorts on click and, where the column has a filter definition,
 * opens the tick list on right-click — the same shape Invoice Detail uses.
 */
function SortableTh({ field, label, sort, onSort, cols }) {
  const headerProps = cols ? cols.headerProps(field) : {}; 
  const isSorted = sort.field === field;
  return (
    <th
      {...headerProps}
      data-col={field}
      className={`sortable${isSorted ? ' is-sorted' : ''}${headerProps.className ? ` ${headerProps.className}` : ''}`}
      onClick={() => onSort(field)}
    >
      {label}
      <span className="sort-arrow material-symbols-rounded">
        {isSorted && sort.direction === 'desc' ? 'arrow_downward' : 'arrow_upward'}
      </span>
    </th>
  );
}

function MapOfAgReport({ user, activeTab = 'contacts' }) {
  const [contacts, setContacts] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });

  // Survives navigating away and back — the period and the selection are work
  // in progress, and losing them mid-job is the thing usePersistedState exists
  // to prevent.
  const [selectedIds, setSelectedIds] = usePersistedState('mapOfAg.selected', []);
  const [filterProcessor, setFilterProcessor] = usePersistedState('mapOfAg.filterProcessor', 'all');
  const [search, setSearch] = usePersistedState('mapOfAg.search', '');
  const [page, setPage] = usePersistedState('mapOfAg.page', 1);
  const [sort, setSort] = usePersistedState('mapOfAg.sort', { field: 'customer', direction: 'asc' });
  const [pageSize, setPageSize] = usePersistedState('mapOfAg.pageSize', DEFAULT_PAGE_SIZE);
  const [from, setFrom] = usePersistedState('mapOfAg.from', '');
  const [to, setTo] = usePersistedState('mapOfAg.to', '');

  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  // The queue of items with no formulation, walked one at a time.
  const [fixQueue, setFixQueue] = useState([]);
  const [fixIndex, setFixIndex] = useState(0);
  const [components, setComponents] = useState([]);
  // Products that have a formulation — what an item with none can borrow from.
  const [formulatedItems, setFormulatedItems] = useState([]);
  const [missingOpen, setMissingOpen] = useState(false);
  const [borrowFor, setBorrowFor] = useState(null);
  // Left out of THIS report only — never persisted, and cleared whenever the
  // period or the selection changes, since it is a decision about one report.
  const [excluded, setExcluded] = useState([]);

  const say = (message, type = 'info') => setAlert({ isOpen: true, message, type });
  const { openEntity } = useEntityModal();
  const runPreviewRef = useRef(null);

  const toggleSort = (field) => setSort((prev) => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, f] = await Promise.all([
        fetch(`${API_URL}/api/map-of-ag/contacts`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API_URL}/api/map-of-ag/processors`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API_URL}/api/map-of-ag/formulated-items`, { credentials: 'include' }).then((r) => r.json())
      ]);
      setContacts(c.contacts || []);
      setProcessors(p.processors || []);
      setFormulatedItems(f.items || []);
    } catch (e) {
      say(`Could not load contacts: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * The registry the add-formulation modal needs. Refetchable, because the
   * modal creates new fields and a field that is not in this list never renders
   * — which is why a newly added one used to vanish.
   */
  const loadFormulationComponents = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/formulations/components`, { credentials: 'include' });
      const d = await r.json();
      setComponents(d.components || []);
    } catch { /* the modal shows its own error */ }
  }, []);

  useEffect(() => {
    if (!fixQueue.length || components.length) return;
    loadFormulationComponents();
  }, [fixQueue.length, components.length, loadFormulationComponents]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (filterProcessor === 'all') { /* everything */ }
      else if (filterProcessor === 'none') { if (c.processor_id) return false; }
      else if (String(c.processor_id) !== String(filterProcessor)) return false;
      if (!term) return true;
      return `${c.account_no} ${c.company_name}`.toLowerCase().includes(term);
    });
  }, [contacts, search, filterProcessor]);

  // Right-click filters run over the already-searched rows; the hook derives
  // its options from what it is given, so the menus describe what is on screen.
  const cols = useColumnFilters(filtered, COLUMN_FILTERS, {
    storageKey: 'mapOfAg.columnFilters',
    onChange: () => setPage(1)
  });

  /*
   * Any filter at all — the search box, the processor dropdown or a column
   * filter — lights the Reset button. The period is not a filter (Reset leaves
   * it alone), so it does not count.
   */
  const headerFilters = activeHeaderFilters(
    { search, processor: filterProcessor },
    { search: '', processor: 'all' }
  );
  const filtersApplied = headerFilters.count > 0 || (cols?.activeCount || 0) > 0;

  const sorted = useMemo(() => {
    const read = SORTERS[sort.field] || SORTERS.customer;
    const dir = sort.direction === 'desc' ? -1 : 1;
    return [...cols.rows].sort((a, b) => {
      const x = read(a);
      const y = read(b);
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return String(a.account_no || '').localeCompare(String(b.account_no || ''));
    });
  }, [cols.rows, sort]);

  const pageRows = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const setChecked = useCallback((id, on) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return [...next];
    });
  }, [setSelectedIds]);
  const drag = useDragSelect({ setChecked });

  // After pageRows and selectedSet: both are consts declared above, and
  // reading either before its declaration throws rather than warning.
  const ctxMenu = useTableContextMenu({
    rows: pageRows,
    selectedIds: selectedSet,
    getRowId: (c) => c.id
  });


  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => selectedSet.has(r.id));
  const toggleAllOnPage = () => {
    const ids = pageRows.map((r) => r.id);
    setSelectedIds((prev) => (allOnPageSelected
      ? prev.filter((id) => !ids.includes(id))
      : [...new Set([...prev, ...ids])]));
  };

  useSlideOutButton([selectedIds.length, busy, preview, activeTab]);

  const assign = async (processorId, accountIds = selectedIds) => {
    if (!accountIds.length) return say('Nothing to assign.', 'warning');
    try {
      const res = await fetch(`${API_URL}/api/map-of-ag/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ accountIds, processorId: processorId || null })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Assign failed');
      await load();
      say(`${data.changed} contact${data.changed === 1 ? '' : 's'} updated.`, 'success');
    } catch (e) {
      say(e.message, 'error');
    }
  };

  // Re-priced as soon as something is left out, so the totals on screen are the
  // totals that would go out.
  useEffect(() => {
    if (preview && from && to && selectedIds.length) runPreviewRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excluded]);

  const runPreview = async () => {
    if (!from || !to) return say('Choose a date range first.', 'warning');
    if (!selectedIds.length) return say('Select at least one contact.', 'warning');
    setBusy('preview');
    try {
      const res = await fetch(`${API_URL}/api/map-of-ag/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ accountIds: selectedIds, from, to, excludeItemCodes: excluded })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setPreview(data);
      if (!data.customers.length) say('Nothing was delivered in that period for those contacts.', 'warning');
      return data;
    } catch (e) {
      say(e.message, 'error');
    } finally {
      setBusy('');
    }
  };

  runPreviewRef.current = runPreview;

  /**
   * Nothing goes out while a product in it still has no formulation and no
   * decision. Excluding one counts as deciding — the figures then simply are
   * not claimed — so the gate is the OUTSTANDING gaps, not all of them.
   */
  const missing = preview?.missing || [];
  const outstanding = missing.filter((m) => !m.excluded).length;
  const omitted = missing.filter((m) => m.excluded).length;
  const borrowed = preview?.borrowed?.length || 0;
  const canExport = !!preview?.customers?.length && outstanding === 0;

  /**
   * What the banner says, for every combination rather than two half-sentences
   * side by side. It used to read "1 with no formulation" AND "1 left out of
   * this report" about the SAME product, which is one fact told twice and
   * sounds like two problems.
   */
  const readiness = (() => {
    const s = (n) => (n === 1 ? '' : 's');
    const has = (n) => (n === 1 ? 'has' : 'have');
    const omittedPart = omitted
      ? `${omitted} product${s(omitted)} without a formulation ${has(omitted)} been left out of this report.`
      : '';
    const borrowedPart = borrowed
      ? `${borrowed} ${borrowed === 1 ? 'is' : 'are'} reporting another product's analysis.`
      : '';

    if (outstanding) {
      return {
        tone: 'warning',
        icon: 'science',
        headline: `${outstanding} product${s(outstanding)} in this report ${has(outstanding)} no formulation.`,
        detail: [
          `Their soya and protein columns would be blank, which a processor will read as zero.`,
          omittedPart,
          borrowedPart
        ].filter(Boolean).join(' ')
      };
    }

    return {
      tone: 'success',
      icon: 'task_alt',
      headline: omitted
        ? 'Every product left in this report has a formulation.'
        : 'Every product in this report has a formulation.',
      detail: [omittedPart, borrowedPart, 'Ready to preview or export.'].filter(Boolean).join(' ')
    };
  })();

  /**
   * Producing the document. `format` comes from the export dialog; a preview is
   * always a PDF, because there is nothing to look at in a browser for a
   * workbook.
   */
  const generate = async (mode, { format = 'xlsx', to: emailTo } = {}) => {
    if (!from || !to) return say('Choose a date range first.', 'warning');
    if (!selectedIds.length) return say('Select at least one contact.', 'warning');
    setBusy(mode);
    try {
      const body = {
        accountIds: selectedIds, from, to, mode, excludeItemCodes: excluded,
        format: mode === 'view' ? 'pdf' : format,
        ...(mode === 'email' ? { to: emailTo || user?.email } : {})
      };
      const res = await fetch(`${API_URL}/api/map-of-ag/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (mode === 'email') {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Send failed');
        setExportOpen(false);
        say(data.message, 'success');
      } else {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Generate failed');
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        {
          const a = document.createElement('a');
          a.href = url;
          // The server names it; this is only the fallback if it did not.
          a.download = `EA_MofA_${from}-${to}.${mode === 'view' ? 'pdf' : format}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }
      }
      await load();
    } catch (e) {
      say(e.message, 'error');
    } finally {
      setBusy('');
    }
  };

  /**
   * Assignment as a row action rather than a dropdown in the filter row.
   * Right-clicking a row with several ticked assigns all of them, which is what
   * the tick boxes were for; right-clicking one that is not ticked assigns just
   * that one.
   */
  const buildRowActions = (contact) => {
    const bulk = selectedSet.has(contact.id) && selectedIds.length > 1;
    const targets = bulk ? selectedIds : [contact.id];
    const suffix = bulk ? ` (${selectedIds.length} contacts)` : '';
    return [
      ...processors.map((p) => ({
        key: `proc-${p.id}`,
        icon: contact.processor_id === p.id && !bulk ? 'radio_button_checked' : 'radio_button_unchecked',
        label: `${p.name}${suffix}`,
        title: `Assign ${p.name}`,
        onClick: () => assign(String(p.id), targets)
      })),
      {
        key: 'clear',
        icon: 'block',
        label: `Clear processor${suffix}`,
        title: 'Leave these contacts unassigned',
        disabled: !bulk && !contact.processor_id,
        onClick: () => assign('', targets)
      }
    ];
  };

  /** Borrow another product's formulation for one that has none. */
  const borrowFormulation = async (missingItem, sourceItemId) => {
    setBorrowFor(null);
    try {
      const res = await fetch(`${API_URL}/api/map-of-ag/formulation-substitutes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          itemId: missingItem.inventory_item_id,
          itemCode: missingItem.item_code,
          sourceItemId: Number(sourceItemId),
          sourceItemCode: formulatedItems.find((i) => i.id === Number(sourceItemId))?.item_code
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save that');
      say(`${missingItem.item_code} will report ${formulatedItems.find((i) => i.id === Number(sourceItemId))?.item_code}'s analysis from now on.`, 'success');
      await runPreview();
    } catch (e) {
      say(e.message, 'error');
    }
  };

  const fixItem = fixQueue[fixIndex] || null;
  /**
   * Saved one. Only claim everything is done when everything IS done — the
   * queue is usually a single item picked off the list, and announcing "all
   * flagged items now have a formulation" after saving one of fifty was simply
   * untrue. The refreshed preview is what gets asked.
   */
  const onFormulationSaved = async () => {
    const next = fixIndex + 1;
    if (next < fixQueue.length) { setFixIndex(next); return; }

    setFixQueue([]);
    setFixIndex(0);
    const data = await runPreview();
    const left = data?.missing?.length ?? 0;
    if (left === 0) {
      say('Every product in this report now has a formulation.', 'success');
    } else {
      say(`Saved. ${left} product${left === 1 ? '' : 's'} still without a formulation.`, 'success');
      // Straight back to the list, so the next one is a click away.
      setMissingOpen(true);
    }
  };

  if (activeTab === 'processors') {
    return <ProcessorSettings processors={processors} onChanged={load} />;
  }
  if (activeTab === 'product-mapping') {
    return <FormulationMappingSettings />;
  }
  if (activeTab === 'history') {
    return <MapOfAgHistory user={user} />;
  }

  return (
    <div className="map-of-ag-report invoice-detail-report">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Map of Ag</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Tonnes delivered per product, with the soya and protein figures from the
              formulation live on the delivery date. Right-click a contact — or use the menu
              at the end of its row — to set its processor; tick several first to set them
              all at once.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={() => { setSelectedIds([]); setPreview(null); setSearch(''); setFilterProcessor('all'); cols.clearEverything(); }}
                disabled={!!busy}
                title={filtersApplied ? 'Clear the selection, the filters and the preview' : 'No filters applied'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Reset</span>
              </button>
            </div>
            <div>
              <button
                className="btn-slideout"
                onClick={runPreview}
                disabled={busy === 'preview' || !selectedIds.length || !from || !to}
                title={!selectedIds.length ? 'Select the contacts to report on'
                  : (!from || !to) ? 'Choose a date range' : 'Check the period and see what is missing'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">fact_check</span></div>
                <span className="btn-label">{busy === 'preview' ? 'Checking…' : 'Check & preview'}</span>
              </button>
            </div>
            {/* Not merely disabled — absent. Until every product has a
                formulation or has been left out, exporting is not a thing that
                should be offered, and a greyed button invites a hunt for the
                reason. The banner carries the reason instead. */}
            {canExport && (
              <>
                <div>
                  <button
                    className="btn-slideout"
                    onClick={() => setPreviewOpen(true)}
                    disabled={!!busy}
                    title="See it as it will print"
                  >
                    <div className="icon-wrapper"><span className="material-symbols-rounded">visibility</span></div>
                    <span className="btn-label">Preview</span>
                  </button>
                </div>
                <div>
                  <button
                    className="btn-slideout"
                    onClick={() => setExportOpen(true)}
                    disabled={!!busy}
                    title="Choose a format and download or email it"
                  >
                    <div className="icon-wrapper"><span className="material-symbols-rounded">ios_share</span></div>
                    <span className="btn-label">Export…</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>From</label>
            <DateInput value={from} onChange={(e) => { setFrom(e.target.value); setPreview(null); setExcluded([]); }} />
          </div>
          <div className="filter-group">
            <label>To</label>
            <DateInput value={to} onChange={(e) => { setTo(e.target.value); setPreview(null); setExcluded([]); }} />
          </div>
          <div className="filter-group">
            <label>Processor</label>
            <Dropdown
              value={filterProcessor}
              onChange={(v) => { setFilterProcessor(v); setPage(1); }}
              options={[
                { value: 'all', label: 'All processors' },
                { value: 'none', label: '— Not assigned —' },
                ...processors.map((p) => ({ value: String(p.id), label: p.name }))
              ]}
            />
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={String(pageSize)}
              onChange={(v) => { setPageSize(Number(v)); setPage(1); }}
              options={[
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
            />
          </div>
          <div className="filter-group">
            <label>Search</label>
            <Input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Account number or name…"
            />
          </div>
          <div className="filter-group selection-cell">
            <label>Selected</label>
            <div className="selection-count">
              {selectedIds.length} of {sorted.length}
              <button
                className="btn-icon"
                onClick={() => setSelectedIds(sorted.map((c) => c.id))}
                title="Select every contact matching the filters"
              >
                <span className="material-symbols-rounded">select_all</span>
              </button>
              <button
                className="btn-icon"
                onClick={() => setSelectedIds([])}
                disabled={!selectedIds.length}
                title="Clear the selection"
              >
                <span className="material-symbols-rounded">deselect</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {preview && (
        <div className="report-preview">
          <div className="preview-head">
            <span className="preview-summary">
              <strong>{preview.customers.length}</strong> contact{preview.customers.length === 1 ? '' : 's'}
              <span className="sep">·</span>
              <strong>{t4(preview.customers.reduce((s, c) => s + Number(c.tonnes || 0), 0))} T</strong>
              <span className="sep">·</span>
              {preview.meta.lines} deliveries
              {preview.borrowed?.length > 0 && (
                <span className="preview-flag is-borrowed" title={preview.borrowed.map((b) => `${b.item_code} → ${b.from_code}`).join('\n')}>
                  {preview.borrowed.length} borrowing another product's analysis
                </span>
              )}
              {preview.meta.analysis_after_delivery > 0 && (
                <span className="preview-flag" title="The only formulation on file was written after the delivery, so its figures are used">
                  {preview.meta.analysis_after_delivery} analysed after delivery
                </span>
              )}
            </span>
            <button className="btn-icon" onClick={() => setPreview(null)} title="Close preview">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>

          <div className={`missing-head is-${readiness.tone}`}>
            <span className="material-symbols-rounded">{readiness.icon}</span>
            <strong>{readiness.headline}</strong>
            <span className="missing-note">{readiness.detail}</span>
            {(missing.length > 0 || borrowed > 0) && (
              <div className="buttons slideout">
                <div>
                  <button className="btn-slideout" onClick={() => setMissingOpen(true)}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">rule</span></div>
                    <span className="btn-label">Review them</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <>
          <div className="report-pagination">
            <PaginationControls
              pagination={{
                page,
                limit: pageSize,
                total: sorted.length,
                totalPages: Math.max(1, Math.ceil(sorted.length / pageSize))
              }}
              onPageChange={setPage}
            />
          </div>
          <div className="table-container">
            <table className="data-table map-of-ag-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th className="checkbox-col no-sort">
                    <TickCheckbox
                      id="moa-select-all"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      ariaLabel="Select all on this page"
                    />
                  </th>
                  <SortableTh field="customer" label="Customer" sort={sort} onSort={toggleSort} cols={cols} />
                  <SortableTh field="processor" label="Processor" sort={sort} onSort={toggleSort} cols={cols} />
                  {/* Last reported sorts but does not filter: a column of
                      distinct timestamps makes a useless tick list. */}
                  <SortableTh field="reported" label="Last reported" sort={sort} onSort={toggleSort} />
                  {/* No heading: three dots need no title. */}
                  <th className="actions-header no-sort" />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((c) => {
                  const on = selectedSet.has(c.id);
                  return (
                    <tr
                      key={c.id}
                      className={`line-row${on ? ' selected' : ''}`}
                    >
                      <td className="checkbox-col">
                        <TickCheckbox
                          id={`moa-${c.id}`}
                          checked={on}
                          onChange={(e) => setChecked(c.id, e.target.checked)}
                          onMouseDown={drag.onMouseDown(c.id, on)}
                          onMouseEnter={drag.onMouseEnter(c.id)}
                          ariaLabel={`Select ${c.company_name}`}
                        />
                      </td>
                      <td data-col="customer">
                        <button
                          type="button"
                          className="customer-cell"
                          onClick={() => openEntity('contact', c.id)}
                          title={`Open ${c.company_name}`}
                        >
                          <span className="account-badge-inline">{c.account_no}</span>
                          <span className="customer-name">{c.company_name}</span>
                        </button>
                      </td>
                      <td data-col="processor">
                        {c.processor_name
                          ? <span className="status-pill processor-pill">{c.processor_name}</span>
                          : <span className="muted">—</span>}
                      </td>
                      <td data-col="reported">
                        {c.last_reported_at ? formatDateShort(c.last_reported_at) : <span className="muted">Never</span>}
                      </td>
                      <td>
                        {/* The same actions as the right-click menu, for anyone
                            who would rather not right-click. */}
                        <div className="actions-cell">
                          <RowActionsMenu actions={buildRowActions(c)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!pageRows.length && (
                  <tr><td colSpan={5} className="report-empty">No contacts match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ColumnFilterMenu cols={cols} />

      {/* Row actions first, then the table's own, with the divider the menu
          puts between its groups — the same shape as Complaints and the rest. */}
      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          {
            key: 'select-all',
            readSafe: true,
            icon: allOnPageSelected ? 'deselect' : 'select_all',
            label: allOnPageSelected ? 'Deselect all on page' : 'Select all on page',
            disabled: !pageRows.length,
            onClick: toggleAllOnPage
          },
          {
            key: 'select-every',
            readSafe: true,
            icon: 'done_all',
            label: `Select all ${sorted.length} matching`,
            disabled: !sorted.length,
            onClick: () => setSelectedIds(sorted.map((c) => c.id))
          },
          {
            key: 'clear-selection',
            readSafe: true,
            icon: 'deselect',
            label: 'Clear selection',
            disabled: !selectedIds.length,
            onClick: () => setSelectedIds([])
          },
          {
            key: 'preview',
            readSafe: true,
            icon: 'fact_check',
            label: 'Check & preview',
            disabled: !selectedIds.length || !from || !to || busy === 'preview',
            onClick: runPreview
          },
          {
            key: 'clear-filters',
            readSafe: true,
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: () => { setSearch(''); setFilterProcessor('all'); cols.clearEverything(); }
          },
          { key: 'refresh', readSafe: true, icon: 'refresh', label: 'Refresh', onClick: load }
        ]}
      />

      <MissingFormulationsModal
        isOpen={missingOpen}
        missing={preview?.missing || []}
        borrowed={preview?.borrowed || []}
        excluded={excluded}
        onExcludeChange={setExcluded}
        onAddFormulation={(m) => { setMissingOpen(false); setFixQueue([m]); setFixIndex(0); }}
        onBorrow={(m) => setBorrowFor(m)}
        onClose={() => setMissingOpen(false)}
      />

      <FormulationSourceModal
        isOpen={!!borrowFor}
        forItem={borrowFor}
        items={formulatedItems}
        onPick={(sourceId) => borrowFormulation(borrowFor, sourceId)}
        onClose={() => setBorrowFor(null)}
      />

      {fixItem && (
        <FormulationModal
          mode="add"
          formulationId={null}
          components={components}
          onComponentsChanged={loadFormulationComponents}
          initialItemId={fixItem.inventory_item_id}
          initialItemLabel={`${fixItem.item_code} — ${fixItem.item_name}`}
          queueProgress={{ index: fixIndex + 1, total: fixQueue.length }}
          onClose={() => { setFixQueue([]); setFixIndex(0); }}
          onSaved={onFormulationSaved}
        />
      )}

      <DocumentPreviewModal
        isOpen={previewOpen}
        endpoint="/api/map-of-ag/preview-document"
        payload={{ accountIds: selectedIds, from, to, excludeItemCodes: excluded }}
        title="Map of Ag report"
        onClose={() => setPreviewOpen(false)}
      />

      <MapOfAgExportModal
        isOpen={exportOpen}
        busy={!!busy}
        defaultEmail={user?.email || ''}
        onExport={({ format, mode, to }) => generate(mode, { format, to })}
        onClose={() => setExportOpen(false)}
      />

      {confirm && <ConfirmDialog isOpen {...confirm} onCancel={() => setConfirm(null)} />}

      <AlertDialog
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, isOpen: false })}
      />
    </div>
  );
}

export default MapOfAgReport;
